import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Search, RefreshCw, Loader2, ArrowUp, ArrowDown, FlaskConical, X,
} from 'lucide-react';
import { automationService, type ScenarioSummary } from '@/services/strategyService';
import { ScenarioCard } from './ScenarioCard';

const PAGE_SIZE = 24;

// Sort presets map onto the server-side sortKeys accepted by
// GET /api/Scenario/page — sorting happens in the DB across the WHOLE set,
// not just the loaded page.
const SORTS: { id: string; label: string; key: string; defaultDir: 'asc' | 'desc' }[] = [
  { id: 'newest', label: 'Newest', key: 'createdAt', defaultDir: 'desc' },
  { id: 'pnl', label: 'Net P&L', key: 'totalPnl', defaultDir: 'desc' },
  { id: 'winrate', label: 'Win rate', key: 'winRate', defaultDir: 'desc' },
  { id: 'trades', label: 'Trades', key: 'tradeCount', defaultDir: 'desc' },
  { id: 'pf', label: 'Profit factor', key: 'profitFactor', defaultDir: 'desc' },
  { id: 'name', label: 'Name', key: 'name', defaultDir: 'asc' },
];

interface Props {
  onOpen: (id: string) => void;
}

/** Toggleable filter pill. */
const Chip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
      active
        ? 'border-primary/50 bg-primary/15 text-primary'
        : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'
    }`}
  >
    {children}
  </button>
);

export const ScenarioBrowser: React.FC<Props> = ({ onOpen }) => {
  const [items, setItems] = useState<ScenarioSummary[]>([]);
  const [total, setTotal] = useState(0);
  /** Rows the server has handed back so far, before de-duplication. */
  const [fetched, setFetched] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortId, setSortId] = useState('newest');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [completedOnly, setCompletedOnly] = useState(true);
  const [profitableOnly, setProfitableOnly] = useState(false);
  const [minTrades, setMinTrades] = useState(0);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const sort = SORTS.find(s => s.id === sortId) ?? SORTS[0];

  // Guards against an older in-flight page overwriting a newer one when the
  // user changes sort/filters mid-request.
  const reqSeq = useRef(0);
  // Snapshot the first page was served from. Echoed back on every "load more" so
  // the whole scroll walks one stable result set — a batch inserting a run
  // mid-scroll would otherwise shift every row down a slot, repeating one and
  // skipping another. Cleared whenever the query changes, since that's a new set.
  const asOfRef = useRef<string | undefined>(undefined);

  const fetchPage = useCallback(async (offset: number) => {
    const seq = ++reqSeq.current;
    offset === 0 ? setLoading(true) : setLoadingMore(true);
    setError(null);
    try {
      const res = await automationService.listScenariosPaged({
        limit: PAGE_SIZE, offset,
        sortKey: sort.key, sortDir,
        search, completedOnly, profitableOnly, minTrades,
        mode: 'Backtest',
        // offset 0 is a fresh query — let the server stamp a new snapshot.
        asOf: offset === 0 ? undefined : asOfRef.current,
      });
      if (seq !== reqSeq.current) return; // superseded
      if (offset === 0) asOfRef.current = res.asOf;
      setTotal(res.total);
      setFetched(f => (offset === 0 ? res.items.length : f + res.items.length));
      // Belt and braces alongside the asOf snapshot: a deletion mid-scroll can
      // still shorten the set, and de-duplicating on append means a repeated row
      // can never render twice.
      setItems(prev => {
        if (offset === 0) return res.items;
        const seen = new Set(prev.map(s => s.id));
        return [...prev, ...res.items.filter(s => !seen.has(s.id))];
      });
    } catch (err: any) {
      if (seq !== reqSeq.current) return;
      setError(err?.message || String(err));
    } finally {
      if (seq === reqSeq.current) { setLoading(false); setLoadingMore(false); }
    }
  }, [sort.key, sortDir, search, completedOnly, profitableOnly, minTrades]);

  useEffect(() => { fetchPage(0); }, [fetchPage]);

  const pickSort = (id: string) => {
    if (id === sortId) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortId(id);
      setSortDir(SORTS.find(s => s.id === id)?.defaultDir ?? 'desc');
    }
  };

  // Compared against what the server has actually returned, not against
  // items.length: de-duplication can leave the list shorter than the number of
  // rows fetched, and comparing lengths would stall "Load more" early.
  const hasMore = fetched < total;

  return (
    <div className="space-y-4">
      {/* ── Toolbar ── */}
      <Card className="p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search scenarios by name…"
              className="w-full h-9 pl-8 pr-8 rounded-md border border-input bg-background text-sm
                         focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchPage(0)} disabled={loading} title="Refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {/* Sort — click the active one again to flip direction */}
          <div className="flex flex-wrap items-center gap-1">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Sort</span>
            {SORTS.map(s => {
              const active = s.id === sortId;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => pickSort(s.id)}
                  className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                    active ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {s.label}
                  {active && (sortDir === 'asc'
                    ? <ArrowUp className="h-3 w-3" />
                    : <ArrowDown className="h-3 w-3" />)}
                </button>
              );
            })}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-1.5 sm:ml-auto">
            <Chip active={completedOnly} onClick={() => setCompletedOnly(v => !v)}>Completed</Chip>
            <Chip active={profitableOnly} onClick={() => setProfitableOnly(v => !v)}>Profitable</Chip>
            <Chip active={minTrades > 0} onClick={() => setMinTrades(v => (v > 0 ? 0 : 10))}>≥ 10 trades</Chip>
          </div>
        </div>
      </Card>

      {/* ── Results ── */}
      {error && (
        <Card className="p-4 text-sm text-red-500">Failed to load scenarios: {error}</Card>
      )}

      {loading && items.length === 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="h-[210px] animate-pulse bg-muted/40" />
          ))}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <Card className="flex flex-col items-center justify-center gap-2 py-16 text-center">
          <FlaskConical className="h-8 w-8 text-muted-foreground/50" />
          <div className="text-sm font-medium">No scenarios match these filters</div>
          <div className="text-xs text-muted-foreground max-w-sm">
            Run a backtest from the Scenarios or Backtest Lab tab, or loosen the filters above.
          </div>
        </Card>
      )}

      {items.length > 0 && (
        <>
          <div className="flex items-center justify-between text-xs text-muted-foreground px-0.5">
            <span>Showing <span className="tabular-nums font-medium text-foreground">{items.length}</span> of{' '}
              <span className="tabular-nums font-medium text-foreground">{total}</span> scenarios</span>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map(s => (
              <ScenarioCard key={s.id} scenario={s} onOpen={onOpen} />
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center pt-1">
              <Button variant="outline" size="sm" onClick={() => fetchPage(fetched)} disabled={loadingMore}>
                {loadingMore
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…</>
                  : <>Load {Math.min(PAGE_SIZE, total - fetched)} more</>}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ScenarioBrowser;
